import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, PopoverBody, Table, UncontrolledPopover } from "reactstrap";
import Swal from "sweetalert2";
import { expireInvoice } from "../../../slices/admin/thunk";
import { payInvoice, payNextInvoice } from "../../../slices/general/thunk";
import {
  formatDate,
  formatInvoiceDueDateFromEffectiveDate,
  formatMonthAndYear,
  parsedInvoiceAmount,
} from "../../../utils/utils";
import InvoicePaymentStatusBadge from "../../Invoices/InvoicePaymentStatusBadge";
import FooterPagination from "../../Pagination/FooterPagination";
import moment from "moment";

const BillingTable = ({
  data,
  currentUser,
  paginationProps,
  onRefresh,
  invoiceOfMonth,
  loading,
  isAdmin,
  activeTab,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loadingPay, setLoadingPay] = React.useState(false);

  const { id } = useParams();
  const isProfilePage = id;

  const handleRowClick = (invoiceId, employerId, event) => {
    const selection = window.getSelection().toString();

    if (!selection) {
      if (activeTab == "current-month" && isAdmin) {
        navigate(`/employers/${employerId}/invoices/current-month`);
      } else {
        navigate(
          `${isAdmin && isProfilePage ? "/billing/" : ""
          }invoiceDetail/${invoiceId}`
        );
      }
    }
  };

  const invoiceofMonthTotal =
    invoiceOfMonth?.invoiceTotal !== undefined
      ? invoiceOfMonth?.invoiceTotal
      : 0 || invoiceOfMonth?.totalAmount !== undefined
        ? invoiceOfMonth?.totalAmount
        : 0;

  // #region HANDLERS
  const payInvoiceOfMonth = async () => {
    try {
      setLoadingPay(true);
      Swal.fire({
        title: "Processing...",
        html: "Please wait while the invoice is being paid.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      const res = await dispatch(
        payNextInvoice({
          userId: currentUser.id,
        })
      );
      Swal.close();
      if (res.payload.error) {
        Swal.fire({
          title: "Error",
          text: res.payload.error,
          icon: "error",
        });
      } else {
        // Show message about payment processing
        Swal.fire({
          title: "Success",
          text: "Invoice payment authorized and processing. It may take a few minutes to reflect in your account.",
          icon: "success",
        });
        // navigate("/billing");
        onRefresh();
      }
      setLoadingPay(false);
    } catch (error) {
      setLoadingPay(true);
      Swal.fire({
        title: "Error",
        text: error,
        icon: "error",
      });
      console.log(error);
      setLoadingPay(false);
    }
  };

  const payInvoiceById = async (invoice) => {
    try {
      setLoadingPay(true);
      Swal.fire({
        title: "Processing...",
        html: "Please wait while the invoice is being paid.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await dispatch(
        payInvoice({
          userId: currentUser.id,
          invoiceId: invoice.id,
          //||  invoice.stripeInvoiceId,
        })
      );
      Swal.close();

      if (res.payload && res.payload.error) {
        Swal.fire({
          title: "Error",
          text: res.payload.error,
          icon: "error",
        });
      } else {
        // Muestra el mensaje de éxito
        Swal.fire({
          title: "Success",
          text: "Invoice payment authorized and processing. It may take a few minutes to reflect in your account.",
          icon: "success",
        });
        onRefresh();
      }
    } catch (error) {
      Swal.close();
      console.error(error);
      Swal.fire({
        title: "Error",
        text:
          typeof error === "string"
            ? error
            : "An error occurred. Please try again later.",
        icon: "error",
      });
    } finally {
      setLoadingPay(false);
    }
  };

  const handleExpireInvoice = async (e, invoice) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You are about to expire this invoice.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, expire it!",
        cancelButtonText: "No, cancel!",
      });

      if (result.isConfirmed) {
        const res = await dispatch(
          expireInvoice({
            invoiceId: invoice.id,
            // || invoice.stripeInvoiceId,
          })
        );
        console.log("expired invoice response: ", res);
        if (res.error || !res.payload) {
          Swal.fire({
            title: "Error",
            text:
              res.error?.message ||
              "An error occurred. Please try again later.",
            icon: "error",
          });
        } else {
          Swal.fire("Expired!", "Invoice has been expired.", "success");
          onRefresh();
        }
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Error",
        text: error.message || "An error occurred. Please try again later.",
        icon: "error",
      });
    }
  };

  // #region RENDERS
  const renderButtonExpiredNow = (invoice) => {
    if (invoice?.expired || invoice?.paid) return null;

    return (
      <>
        <Button
          onClick={(e) => handleExpireInvoice(e, invoice)}
          color="soft-primary"
          size="sm"
          className="btn py-1 me-1 btn-primary"
        >
          Expire Invoice
        </Button>
      </>
    );
  };

  const renderButtonPayNow = (invoice) => {
    const isCurrentMonthInvoice = !invoice.id;
    const handlePayInvoice = (e) => {
      e.stopPropagation();
      if (isCurrentMonthInvoice) {
        payInvoiceOfMonth();
      } else {
        payInvoiceById(invoice);
        // console.log(invoice.id);
      }
    };

    return (
      <>
        {!invoice.paid && !invoice.paymentProcessing ? (
          <Button
            // onClick={isCurrentMonthPage ? payInvoiceOfMonth : payInvoiceById}
            onClick={handlePayInvoice}
            color="primary"
            size="sm"
            className="btn py-1 btn-primary"
            disabled={loadingPay}
          >
            <i className="ri-add-line align-bottom "></i> Pay Now
          </Button>
        ) : null}{" "}
      </>
    );
  };

  const renderInvoiceOfMonthRow = () => {
    const isRowClickable = invoiceofMonthTotal !== 0;

    const nameColumn = invoiceOfMonth.dashboardUserInfo?.companyName
      ? `${invoiceOfMonth.dashboardUserInfo?.companyName}`
      : `${invoiceOfMonth.dashboardUserInfo?.firstName} ${invoiceOfMonth.dashboardUserInfo?.lastName || ""
      } ${invoiceOfMonth.dashboardUserInfo?.email
        ? `(${invoiceOfMonth.dashboardUserInfo?.email})`
        : ""
      }`;

    const handleClickRow = () => {
      if (isRowClickable) {
        navigate(
          `/employers/${invoiceOfMonth.dashboardUserId}/invoices/current-month`
        );
      }
    };

    return (
      <>
        <tr
          onClick={handleClickRow}
          className={`bg ${isRowClickable ? "cursor-pointer" : ""}`}
        >
          {isAdmin && <td>{nameColumn}</td>}
          <td>{formatMonthAndYear(invoiceOfMonth?.effectiveForDate)}</td>
          <td>{formatDate(invoiceOfMonth?.date)}</td>
          <td>${parsedInvoiceAmount(invoiceofMonthTotal) || "0"}</td>
          <td>
            {formatInvoiceDueDateFromEffectiveDate(
              invoiceOfMonth?.effectiveForDate
            )}
          </td>
          {/* <td
          // className="d-flex align-items-center"
          >
            <div className="d-flex align-items-center">
              <InvoicePaymentStatusBadge invoice={invoiceOfMonth} />

            </div>
          </td> */}

          <td className="text-center">
            <InvoicePaymentStatusBadge invoice={invoiceOfMonth} />
          </td>

          <td className="text-center py-0">
            {isRowClickable ? (
              <Button color="soft-primary" size="sm" onClick={handleClickRow}>
                View
              </Button>
            ) : null}
          </td>
        </tr>
      </>
    );
  };

  function formatPreviousDate(dateString) {
    const date = new Date(dateString);
    date.setDate(date.getDate() - 1); // Resta un día a la fecha
    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return formattedDate;
  }

  return (
    <Table className="table table-hover border table-centered align-middle table-nowrap mb-0">
      <thead className="bg" style={{ background: "var(--thead-bg)" }}>
        <tr>
          {isAdmin && <th scope="col">Name</th>}

          <th scope="col">Coverage Month</th>
          <th scope="col">Invoice Created On</th>
          <th scope="col" style={{ width: "150px" }}>
            Amount
          </th>
          <th scope="col" style={{ width: "200px" }}>
            Billed On
          </th>
          <th scope="col " style={{ width: "200px" }}>
            <div className="d-flex align-items-center justify-content-center">
              Payment Status
              <i
                id="payment-status-info"
                className="ri-information-fill cursor-pointer text-muted ms-1 fs-5"
              ></i>
              <UncontrolledPopover
                trigger="hover"
                placement="right"
                target="payment-status-info"
              >
                <PopoverBody className="shadow-lg ">
                  No action needed. "Not Paid" invoices will be automatically
                  paid on the next billing date.
                </PopoverBody>
              </UncontrolledPopover>
            </div>
          </th>

          <th scope="col" className={"text-center"} style={{ width: "150px" }}>
            Manage
          </th>
        </tr>
      </thead>
      <tbody>
        {invoiceOfMonth && renderInvoiceOfMonthRow()}

        {data?.length > 0 ? (
          data &&
          data?.map((invoice, index) => {
            const invoiceTotal =
              invoice?.invoiceTotal !== undefined
                ? invoice?.invoiceTotal
                : 0 || invoice?.totalAmount !== undefined
                  ? invoice?.totalAmount
                  : 0;

            const dashboardUserInfo = invoice.dashboardUserInfo;
            const nameColumn = invoice.dashboardUserInfo?.companyName
              ? `${dashboardUserInfo?.companyName}`
              : `${dashboardUserInfo?.firstName || invoice.dashboardUserId} ${dashboardUserInfo?.lastName || ""
              } ${dashboardUserInfo?.email
                ? `(${dashboardUserInfo?.email})`
                : ""
              }`;

            const employerId = invoice.dashboardUserId;
            return (
              <tr
                key={index}
                onClick={(event) =>
                  invoiceTotal !== 0 && !invoice?.expired
                    ? // ? handleRowClick(invoice?.stripeInvoiceId, event)
                    handleRowClick(invoice?.id, employerId, event)
                    : null
                }
                className={
                  invoiceTotal !== 0 && !invoice?.expired
                    ? "cursor-pointer"
                    : ""
                }
              >
                {isAdmin && <td>{nameColumn}</td>}
                <td>{formatMonthAndYear(invoice?.effectiveForDate)}</td>
                <td>{moment(invoice?.date).utc().startOf("month").format("MM/DD/YYYY")}</td>
                <td>${parsedInvoiceAmount(invoiceTotal) || "0"}</td>
                <td>
                  {/* {formatInvoiceDueDateFromEffectiveDate(
                    invoice?.effectiveForDate
                  )} */}
                  {invoice?.dateUnix ? moment.unix(invoice?.dateUnix).utc().format("MM/DD/YYYY") : ''}
                </td>
                <td className="text-center">
                  {!invoiceTotal || invoiceTotal === 0 ? null : (
                    <InvoicePaymentStatusBadge invoice={invoice} />
                  )}
                </td>

                <td className="py-0 text-center ">
                  {/* {isAdmin ? renderButtonExpiredNow(invoice) : null} */}
                  {invoiceTotal !== 0 && !invoice?.expired ? (
                    <Button
                      color="soft-primary"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        // handleRowClick(invoice?.stripeInvoiceId, event);
                        handleRowClick(invoice?.id, employerId, event);
                      }}
                    >
                      View
                    </Button>
                  ) : null}
                </td>
              </tr>
            );
          })
        ) : (!loading && !invoiceOfMonth) || !data ? (
          <tr>
            <td className="text-center" colSpan="7">
              No invoices yet
            </td>
          </tr>
        ) : null}
      </tbody>
      {paginationProps.totalItems > 0 && (
        <tfoot>
          <FooterPagination {...paginationProps} />
        </tfoot>
      )}
    </Table>
  );
};

export default BillingTable;
